import {getSisDateAsNumber, getSisDurationAsNumber, getSisDurationBetween24H, getSisTimeAsNumber, parseSisDuration, parseSisTime, RoundMode, roundSisDuration, roundSisTime} from 'util/SisDateTime'
import {CFAttrTO, CustomFieldTO, StTagTO} from 'generated/JavaTO'
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'
import {BSModelType} from 'module/BSCollection/type/BSCollectionStateType'
import {BSAttr} from 'module/BSEntry/BSAttr'
import {CFModel} from 'module/CustomField/type/CFType'
import {CF} from 'module/CustomField/util/CustomField'
import moment from 'moment'

export const RoundStTagPrefix = 'StTag.'
export const RoundDependencyPrefixes = [RoundStTagPrefix]

function resolveCFRoundValue(x?: string, stTag?: StTagTO): string {
	if (!x) return '';
	if (x.startsWith(RoundStTagPrefix)) {
		const field = x.substring(RoundStTagPrefix.length)
		const value = stTag?.[field as keyof StTagTO]
		return value || value === 0 ? value + '' : ''
	} else {
		return x
	}
}

export function resolveCFRoundParameters(cfAttr: CFAttrTO, exceptions?: string[], stTag?: StTagTO) {
	return {
		pulse: +resolveCFRoundValue(cfAttr.value, stTag),
		roundMode: RoundMode[resolveCFRoundValue(cfAttr.type, stTag) as RoundMode],
		exceptions: exceptions?.map(exc => +resolveCFRoundValue(exc, stTag)),
	}
}

export function getCFRoundModelParametersForStTag(model?: BSModelType | CFModel) {
	if (!model) return {}
	const persNr = (model as { persNr?: number }).persNr || (model as & { persNrs?: number[] }).persNrs?.[0]
	const dateTime = BSAttr.getStartDateTime(model as BSModelType)
	const date = getSisDateAsNumber(dateTime)
	return {persNr, date, dateTime}
}

export interface CFRoundResultMapperParams {
	model: CFModel | null | undefined
	allCFDefs: CustomFieldTO[]
	cfDef: CustomFieldTO
	cfAttr: CFAttrTO
	value: number
	pos?: number
	pulse: number
	roundMode: RoundMode
	exceptions?: number[]
}

function findException(value: number, roundedValue: number, exceptions: number[] | undefined, roundMode: RoundMode) {
	if (exceptions) {
		let found;
		switch (roundMode) {
			case RoundMode.floor:
				found = exceptions.find(exc => {
					return value >= exc && Math.abs(value - roundedValue) > Math.abs(value - exc)
				})
				break;
			case RoundMode.ceil:
				found = exceptions.find(exc => {
					return value <= exc && Math.abs(value - roundedValue) > Math.abs(value - exc)
				})
				break;
			case RoundMode.nearest:
				found = exceptions.find(exc => {
					return Math.abs(value - roundedValue) > Math.abs(value - exc)
				})
				break;
		}
		if (found) return found
	}
	return roundedValue
}

export function getCFRoundResult({
	model, allCFDefs, cfDef, cfAttr, value, pos, pulse, roundMode, exceptions,
}: CFRoundResultMapperParams) {
	pulse = +pulse
	roundMode = RoundMode[roundMode]
	let roundedValue: number
	const referencedStartTimeDstField = CF.getCFAttr(cfDef, CFAttrKey.roundTimeEndReferencedStartDstField)?.value
	// round only if exception not matched
	if (cfAttr.key === CFAttrKey.roundTime) {
		const referencedStartTimeCFDef = referencedStartTimeDstField ? allCFDefs.find(x => x.dstField === referencedStartTimeDstField) : undefined
		let timeFromForConvertingEndToDuration: number | undefined
		if (referencedStartTimeCFDef) {
			// convert from duration to time
			const start = parseSisTime(CF.getValueFrom(referencedStartTimeCFDef, model, {pos}) as number, {ignoreSeconds: true}) //remove seconds!
			const dur = parseSisDuration(value)
			value = getSisTimeAsNumber(moment(start).add(dur))
			timeFromForConvertingEndToDuration = getSisTimeAsNumber(start, {ignoreSeconds: true})
		}
		roundedValue = getSisTimeAsNumber(roundSisTime(value, pulse, roundMode))
		roundedValue = findException(value, roundedValue, exceptions, roundMode)
		if (timeFromForConvertingEndToDuration != null) {
			//convert back from time to duration
			roundedValue = getSisDurationAsNumber(getSisDurationBetween24H(timeFromForConvertingEndToDuration, roundedValue))
		}
	} else {
		roundedValue = getSisDurationAsNumber(roundSisDuration(value, pulse, roundMode))
		roundedValue = findException(value, roundedValue, exceptions, roundMode)
	}

	return roundedValue
}